import * as LocalAuthentication from 'expo-local-authentication';

import { analytics } from '../utils/analytics';
import { storeRefreshToken, storeToken, storeTokenExpires } from '../services/storageService';

import {
  postBiometricExchange,
  BiometricInvalidError,
} from './api';
import { mapExpoAuthError, type BiometricPromptErrorReason } from './biometricPromptErrors';
import {
  clearAllBiometricState,
  readBiometricGrant,
  storeBiometricGrant,
} from './biometricStorage';
import { getOrCreateDeviceId } from './deviceId';

export type SignInResult =
  | { ok: true; token: string }
  | {
      ok: false;
      reason:
        | BiometricPromptErrorReason
        | 'no_grant_stored'
        | 'grant_invalid'
        | 'exchange_failed';
    };

// Cold-start biometric sign-in. promptMessage/cancelLabel come from the
// caller so the native biometric sheet respects the user's Transifex locale.
export const attemptBiometricSignIn = async (args: {
  promptMessage: string;
  cancelLabel: string;
}): Promise<SignInResult> => {
  // Verify the grant is actually there BEFORE prompting biometric — no
  // point putting the user through Face ID just to bounce silently. The
  // grant slot is not OS-biometric-gated so this read is a cheap peek.
  const grant = await readBiometricGrant();
  if (!grant) {
    // Enrolled flag may still be set (backup restore / SecureStore
    // corruption); wipe it so we don't re-auto-trigger next cold start.
    await clearAllBiometricState();
    analytics.trackEvent('biometric_signin_failed', { reason: 'no_grant_stored' });
    return { ok: false, reason: 'no_grant_stored' };
  }

  const promptResult = await LocalAuthentication.authenticateAsync({
    promptMessage: args.promptMessage,
    cancelLabel: args.cancelLabel,
    disableDeviceFallback: false,
  });

  if (!promptResult.success) {
    const reason = mapExpoAuthError(promptResult.error);
    analytics.trackEvent('biometric_signin_failed', { reason });
    return { ok: false, reason };
  }

  try {
    // Inside try so SecureStore failure surfaces as exchange_failed rather
    // than an unhandled rejection from the auto-trigger path.
    const deviceId = await getOrCreateDeviceId();

    analytics.trackEvent('biometric_signin_attempted');
    const exchange = await postBiometricExchange({ grant, deviceId });

    await storeToken(exchange.token);
    await storeTokenExpires(exchange.expires);
    // SecureStore rejects a non-string value; only store when present.
    if (exchange.refresh) {
      await storeRefreshToken(exchange.refresh);
    }
    await storeBiometricGrant(exchange.grant_token);

    analytics.trackEvent('biometric_signin_succeeded');
    return { ok: true, token: exchange.token };
  } catch (err) {
    if (err instanceof BiometricInvalidError) {
      analytics.trackEvent('biometric_signin_failed', { reason: 'grant_invalid' });
      await clearAllBiometricState();
      return { ok: false, reason: 'grant_invalid' };
    }
    analytics.trackEvent('biometric_signin_failed', { reason: 'exchange_failed' });
    return { ok: false, reason: 'exchange_failed' };
  }
};
